import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";

// FireBase
import * as firebase from "firebase/app";
import "firebase/app";
import "firebase/firestore";
import "firebase/auth";
import "firebase/functions";
import "firebase/analytics";

// Bootstrap Vue
import { BootstrapVue, DropdownPlugin, TablePlugin } from "bootstrap-vue";
Vue.use(BootstrapVue);
Vue.use(DropdownPlugin);
Vue.use(TablePlugin);

import VueClipboard from "vue-clipboard2";
Vue.use(VueClipboard);

// FontAwesome
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
library.add(fas);
library.add(far);

Vue.component("font-awesome-icon", FontAwesomeIcon);

// Sentry
import * as Sentry from "@sentry/browser";
import { Vue as VueIntegration } from "@sentry/integrations";
import { Integrations } from "@sentry/tracing";

import VueTippy from "vue-tippy";
Vue.use(VueTippy, {
  distance: 5,
});

import SVGInjectorVue from "svginjector-vue";
Vue.use(SVGInjectorVue);
Vue.config.productionTip = false;

import PerfectScrollbar from "vue2-perfect-scrollbar";
import "vue2-perfect-scrollbar/dist/vue2-perfect-scrollbar.css";
Vue.use(PerfectScrollbar);

import Multiselect from "vue-multiselect";
Vue.component("multiselect", Multiselect);

import VueToast from "vue-toast-notification";
import "vue-toast-notification/dist/index.css";

Vue.use(VueToast, {
  position: "top",
  type: "error",
});

import axios from "axios";
import VueAxios from "vue-axios";

Vue.use(VueAxios, axios);

import VuejsDialog from "vuejs-dialog";
Vue.use(VuejsDialog);

import Global from "@/global.js";
Vue.mixin(Global);

const config = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.VUE_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
  measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
};

firebase.initializeApp(config);

firebase.analytics();

if (process.env.VUE_APP_ENV !== "DEV") {
  Sentry.init({
    dsn:
      "https://7f2b17e78c424948b6bfae2d8b2519cf@o352025.ingest.sentry.io/5468190",
    integrations: [
      new VueIntegration({
        Vue,
        tracing: true,
      }),
      new Integrations.BrowserTracing(),
    ],
    tracesSampleRate: 1.0,
  });
}

export const db = firebase.firestore();

import { mapState } from "vuex";
new Vue({
  router,
  store,
  computed: {
    ...mapState(["user"]),
    getUser() {
      return this.user ? this.user : "";
    },
  },
  data() {
    return {
      unsubscribeUser: null,
      unsubscribeGroups: null,
      unsubscribeOrganisation: null,
    };
  },
  created() {
    firebase.auth().onAuthStateChanged((user) => {
      store.commit("updateUser", { user });
      // attach listeners
      if (user && !this.listenersActive) {
        this.$attachPrimaryListeners(user);
      }
      // redirect rules
      if (user) {
        if (this.$router.currentRoute.path == "/sign-in") {
          this.$router.replace({ path: "/" });
        }
      }
    });
  },
  methods: {
    $attachPrimaryListeners(user) {
      // Get and watch userdata
      this.unsubscribeUser = db
        .collection("groupAdmins")
        .doc(user.uid)
        .onSnapshot((doc) => {
          const userData = doc.data();
          if (!userData.updatedDetails) {
            this.$router.push({
              path: "/profile",
            });
          }
          if (!userData.organisation) {
            this.$toast.open({
              message:
                "Your profile has not been linked to an organisation. Please try again soon or get in touch with Traitstack admins (hello@traitstack.com).",
              duration: 999999999,
              position: "bottom-right",
              dismissible: true,
              type: "info",
            });
            this.$signOutAndDetachListeners();
          }
          store.commit("updateUserData", { userData });
          this.$attachSecondaryListeners(userData);
        });
    },
    $attachSecondaryListeners(userData) {
      // Get and watch organisation data
      this.unsubscribeOrganisation = db
        .collection("organisations")
        .doc(userData.organisation)
        .onSnapshot((doc) => {
          const id = doc.id;
          const organisationData = doc.data();
          organisationData.id = id;
          store.commit("updateOrganisationData", { organisationData });
        });

      // Get and watch organisation data
      this.unsubscribeGroups = db
        .collectionGroup("groups")
        .where("organisation", "==", userData.organisation)
        .onSnapshot((querySnapshot) => {
          var groupsData = [];
          querySnapshot.forEach(function(doc) {
            const id = doc.id;
            let data = doc.data();
            data.id = id;
            groupsData.push(data);
          });
          store.commit("updateGroupsData", { groupsData });
        });
    },
    $detachListeners() {
      this.unsubscribeUser();
      this.unsubscribeOrganisation();
      this.unsubscribeGroups();
    },
    $signOutAndDetachListeners() {
      firebase
        .auth()
        .signOut()
        .then(
          () => {
            this.$detachListeners();
            this.loginFunctionInit = false;
            this.$router.replace("/sign-in");
          },
          (error) => {
            console.error("Sign Out Error", error);
          }
        );
    },
  },
  render: (h) => h(App),
}).$mount("#app");
